// Compact coin picker chip used in widget headers. The trigger stays a
// tight chip; the dropdown mirrors the global HeaderSearch UX — searchable
// list with coin icons + name, popular coins first.

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo, useId } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAvailableCoins } from './useAvailableCoins';
import { useCoinIcons } from './useCoinIcons';
import { DEFAULT_COIN_ICON } from '../../utils/helpers';

const POPULAR_FIRST = ['BTC', 'ETH', 'SOL', 'BNB', 'XRP', 'DOGE', 'ADA', 'AVAX', 'HYPE'];
// Shared with sectionHelpers' FilterDropdown so opening this picker closes
// every other open dropdown (and vice versa). Keep the event name in sync
// with that file.
const FILTER_OPEN_EVENT = 'ct-filter-dropdown-open';

function WidgetCoinPicker({ value, onChange }) {
  const { t } = useTranslation();
  const id = useId();
  const [open, setOpen] = useState(false);

  // Close on cross-dropdown broadcast.
  useEffect(() => {
    const handler = (e) => { if (e.detail !== id) setOpen(false); };
    document.addEventListener(FILTER_OPEN_EVENT, handler);
    return () => document.removeEventListener(FILTER_OPEN_EVENT, handler);
  }, [id]);

  const toggleOpen = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      if (next) document.dispatchEvent(new CustomEvent(FILTER_OPEN_EVENT, { detail: id }));
      return next;
    });
  }, [id]);
  const [search, setSearch] = useState('');
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const inputRef = useRef(null);
  const [menuPos, setMenuPos] = useState(null);
  const allCoins = useAvailableCoins();
  const { getCoinIcon } = useCoinIcons();

  useEffect(() => {
    if (!open) return undefined;
    const off = (e) => {
      if (triggerRef.current?.contains(e.target)) return;
      if (menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, [open]);

  // Re-measure the trigger position whenever the menu opens — portal'd
  // menu uses position:fixed so it sits outside the widget's overflow cage.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, left: rect.left });
  }, [open]);

  // Auto-focus search input on open + clear stale query.
  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
    } else {
      setSearch('');
    }
  }, [open]);

  const handlePick = useCallback((coin) => {
    onChange(coin);
    setOpen(false);
    setSearch('');
  }, [onChange]);

  // Filtered + ordered list, mirroring HeaderSearch: popular caps first,
  // then alphabetical rest. Cap at 40 to keep menu tractable on mobile.
  const filtered = useMemo(() => {
    const term = search.trim().toUpperCase();
    if (term) {
      return allCoins.filter((c) => c.toUpperCase().includes(term)).slice(0, 40);
    }
    const popularSet = new Set(POPULAR_FIRST);
    const popular = POPULAR_FIRST.filter((c) => allCoins.includes(c));
    const rest = allCoins.filter((c) => !popularSet.has(c)).slice(0, 30);
    return [...popular, ...rest];
  }, [search, allCoins]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const term = search.trim().toUpperCase();
      if (allCoins.includes(term)) {
        handlePick(term);
      } else if (filtered.length > 0) {
        handlePick(filtered[0]);
      } else if (term) {
        handlePick(term);
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  }, [search, allCoins, filtered, handlePick]);

  return (
    <div className={`widget-coin-picker ${open ? 'open' : ''}`} ref={triggerRef}>
      <button
        type="button"
        className="widget-coin-picker-chip"
        onClick={(e) => { e.stopPropagation(); toggleOpen(); }}
        onMouseDown={(e) => e.stopPropagation()}
        title={t('chartPage.changeCoin', 'Change coin for this widget')}
      >
        <span className="widget-coin-picker-value">{value || '—'}</span>
        <span className="widget-coin-picker-caret">▾</span>
      </button>
      {open && menuPos && typeof document !== 'undefined' && createPortal(
        <div
          ref={menuRef}
          className="widget-coin-picker-menu widget-coin-picker-menu-portal"
          style={{ position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 10000 }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="widget-coin-picker-search">
            <svg className="widget-coin-picker-search-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width="13" height="13" aria-hidden="true">
              <path d="M18.031 16.6168L22.3137 20.8995L20.8995 22.3137L16.6168 18.031C15.0769 19.263 13.124 20 11 20C6.032 20 2 15.968 2 11C2 6.032 6.032 2 11 2C15.968 2 20 6.032 20 11C20 13.124 19.263 15.0769 18.031 16.6168ZM16.0247 15.8748C17.2475 14.6146 18 12.8956 18 11C18 7.1325 14.8675 4 11 4C7.1325 4 4 7.1325 4 11C4 14.8675 7.1325 18 11 18C12.8956 18 14.6146 17.2475 15.8748 16.0247L16.0247 15.8748Z"/>
            </svg>
            <input
              ref={inputRef}
              type="text"
              className="widget-coin-picker-input"
              placeholder={t('header.searchCoins', 'Search coins...')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleKeyDown}
            />
          </div>
          <div className="widget-coin-picker-options">
            {filtered.map((coin) => (
              <button
                key={coin}
                type="button"
                className={`widget-coin-picker-option ${coin === value ? 'active' : ''}`}
                onClick={() => handlePick(coin)}
              >
                <img
                  src={getCoinIcon(coin)}
                  alt=""
                  className="widget-coin-picker-option-icon"
                  loading="lazy"
                  onError={(e) => { e.target.src = DEFAULT_COIN_ICON; }}
                />
                <span className="widget-coin-picker-option-name">{coin}</span>
              </button>
            ))}
            {filtered.length === 0 && search && (
              <button
                type="button"
                className="widget-coin-picker-option widget-coin-picker-option-custom"
                onClick={() => handlePick(search.trim().toUpperCase())}
              >
                {t('chartPage.useCustomCoin', 'Use "{{coin}}"', { coin: search.trim().toUpperCase() })}
              </button>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default WidgetCoinPicker;
